import React from 'react';
import InfoTip from './InfoTip';
import { EMPTY_TEXT, LOADING_TEXT } from './copy';
import { cx, Skeleton } from './ui';

// Generic dashboard tile: the `.card` surface plus an optional header (title,
// subtitle, help, actions) and loading / empty states, so a dashboard does not
// repeat that scaffolding for every widget.
//
// Props:
//   title     — string, rendered as <h3>. Sentence case, per components/copy.js.
//   subtitle  — ONE line, under ~60 chars, saying what the number is. Caveats
//               belong in `help`, not here.
//   help      — optional detail, rendered as an InfoTip beside the title: the
//               retention windows, "why this differs from that card" and other
//               things a reader needs once.
//   actions   — node rendered top-right (a toggle, a "view all" link)
//   loading   — replaces children with a skeleton shaped like a chart
//   empty     — when truthy, replaces children with a centred muted message.
//               Default EMPTY_TEXT; pass a string only when empty genuinely
//               means something different.
//   span      — 1..12, the dashboard grid column span
//   tone      — 'danger' | 'warning' tints the title
//   className, style — escape hatches for the rare layout exception
export default function Card({
  title, subtitle, help, actions, loading, empty, children,
  span, tone, style, className,
}) {
  return (
    <div
      className={cx('card', 'dashboard-card', span && `dashboard-tile-${span}`,
        tone && `tile-tone-${tone}`, className)}
      style={style}
    >
      {(title || subtitle || actions) && (
        <div className="tile-head">
          <div className="tile-head-text">
            {title && (
              <h3 className="tile-title">
                {title}
                {/* `title` as the accessible name: a dashboard puts 20+ of these on
                    one page and "More information" would announce the same for all. */}
                {help && <InfoTip label={title}>{help}</InfoTip>}
              </h3>
            )}
            {subtitle && <div className="tile-subtitle">{subtitle}</div>}
          </div>
          {actions && <div className="tile-actions">{actions}</div>}
        </div>
      )}
      {loading ? (
        <div className="tile-state" role="status" aria-busy="true">
          <span className="sr-only">{LOADING_TEXT}</span>
          <Skeleton height={120} radius={8} />
        </div>
      ) : empty ? (
        <div className="tile-state tile-empty">{empty === true ? EMPTY_TEXT : empty}</div>
      ) : (
        children
      )}
    </div>
  );
}
